import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Heading,
  Icon,
  VStack,
  HStack,
  Progress,
  Divider,
  Badge,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import {
  FaBalanceScale,
  FaFolderOpen,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUsers,
  FaUserTie,
  FaBriefcase,
  FaChartLine
} from 'react-icons/fa';
import api from '../../../../services/api';

const StatCard = ({ icon, label, value, helpText, increase, color = "blue" }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue(`${color}.200`, `${color}.600`);
  
  return (
    <Stat
      px={6}
      py={5}
      bg={bgColor}
      shadow="sm"
      rounded="lg"
      borderLeft="4px"
      borderColor={borderColor}
    >
      <HStack justify="space-between" mb={2}>
        <Icon as={icon} w={8} h={8} color={`${color}.500`} />
        <Badge colorScheme={color} fontSize="xs">
          Departamento
        </Badge>
      </HStack>
      <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
      <StatNumber fontSize="2xl" fontWeight="bold">{value}</StatNumber>
      {helpText && (
        <StatHelpText>
          {increase !== undefined && (
            <StatArrow type={increase ? 'increase' : 'decrease'} />
          )}
          {helpText}
        </StatHelpText>
      )}
    </Stat>
  );
};

const GestorCard = ({ gestor }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  
  return (
    <Box bg={bgColor} p={4} rounded="lg" shadow="sm">
      <HStack justify="space-between" mb={3}>
        <HStack>
          <Icon as={FaUserTie} color="blue.500" />
          <Text fontWeight="bold">{gestor.name}</Text>
        </HStack>
        <Badge colorScheme="green">{gestor.teamSize} membros</Badge>
      </HStack>
      
      <VStack align="stretch" spacing={2}>
        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="xs" color="gray.500">Produtividade</Text>
            <Text fontSize="xs" fontWeight="bold">{gestor.productivity}%</Text>
          </HStack>
          <Progress value={gestor.productivity} size="sm" colorScheme="blue" />
        </Box>
        
        <HStack justify="space-between" fontSize="sm">
          <Text color="gray.600">Em análise:</Text>
          <Text fontWeight="bold">{gestor.inProgress}</Text>
        </HStack>
        
        <HStack justify="space-between" fontSize="sm">
          <Text color="gray.600">Concluídos:</Text>
          <Text fontWeight="bold" color="green.500">{gestor.completed}</Text>
        </HStack>
      </VStack>
    </Box>
  );
};

const DiretoriaJuridicoDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departmentStats, setDepartmentStats] = useState(null);
  const [gestores, setGestores] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [deptResponse, gestoresResponse] = await Promise.all([
        api.get('/juridico/diretoria/department'),
        api.get('/juridico/diretoria/gestores')
      ]);
      
      const deptData = deptResponse.data || deptResponse;
      const gestoresData = gestoresResponse.data || gestoresResponse;
      
      setDepartmentStats(deptData.stats || deptData);
      setGestores(Array.isArray(gestoresData) ? gestoresData : (gestoresData.gestores || []));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Carregando dashboard...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const stats = departmentStats || {
    totalPrecatorios: 0,
    disponiveisAnalise: 0,
    emAnalise: 0,
    aprovados: 0,
    reprovados: 0,
    totalTeamMembers: 0,
    totalGestores: 0,
    avgProductivity: 0
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={FaBalanceScale} boxSize={6} color="blue.600" />
            <Heading size="lg">Dashboard - Diretoria Jurídica</Heading>
          </HStack>
          <Text color="gray.600">
            Visão geral do departamento jurídico
          </Text>
        </Box>

        <Grid templateColumns="repeat(4, 1fr)" gap={6}>
          <GridItem>
            <StatCard
              icon={FaFolderOpen}
              label="Total Precatórios"
              value={stats.totalPrecatorios || 0}
              color="blue"
            />
          </GridItem>
          
          <GridItem>
            <StatCard
              icon={FaClock}
              label="Disponíveis Análise"
              value={stats.disponiveisAnalise || 0}
              helpText="Aguardando designação"
              color="yellow"
            />
          </GridItem>
          
          <GridItem>
            <StatCard
              icon={FaBriefcase}
              label="Em Análise"
              value={stats.emAnalise || 0}
              color="orange"
            />
          </GridItem>
          
          <GridItem>
            <StatCard
              icon={FaCheckCircle}
              label="Aprovados"
              value={stats.aprovados || 0}
              helpText={`${stats.reprovados || 0} reprovados`}
              color="green"
            />
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
          <GridItem>
            <StatCard
              icon={FaUserTie}
              label="Gestores"
              value={stats.totalGestores || gestores.length}
              color="purple"
            />
          </GridItem>
          
          <GridItem>
            <StatCard
              icon={FaUsers}
              label="Equipe Total"
              value={stats.totalTeamMembers || 0}
              helpText="Analistas jurídicos"
              color="teal"
            />
          </GridItem>
          
          <GridItem>
            <StatCard
              icon={FaChartLine}
              label="Produtividade Média"
              value={`${Math.round(stats.avgProductivity || 0)}%`}
              helpText="Este mês"
              increase={true}
              color="green"
            />
          </GridItem>
        </Grid>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>
            <HStack>
              <Icon as={FaUserTie} />
              <Text>Gestores Jurídicos</Text>
            </HStack>
          </Heading>
          
          {gestores.length > 0 ? (
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              {gestores.map((gestor, index) => (
                <GridItem key={gestor._id || index}>
                  <GestorCard gestor={gestor} />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <Alert status="info" rounded="md">
              <AlertIcon />
              Nenhum gestor encontrado no departamento.
            </Alert>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default DiretoriaJuridicoDashboard;